// This file holds small pure functions used by the deal create/edit form.

import { FieldDefinition, StepDefinition } from "../../types/types";
import {
  DeadlineRule,
  FormValue,
  TemplateStep,
} from "../../pages/deals/createDeal/dealFormTypes";
import { normalizeOptionsSource } from "./dealNormalizers";

// Walks every field (including nested groups) and collects the optionsSource
// values so the form knows which dropdown API endpoints to preload.
export function collectOptionsSources(
  fields: FieldDefinition[] = [],
  sources = new Set<string>(),
): Set<string> {
  fields.forEach((field) => {
    const source = normalizeOptionsSource(field?.optionsSource);
    if (source) sources.add(source);

    if (Array.isArray(field?.fields)) {
      collectOptionsSources(field.fields, sources);
    }
  });

  return sources;
}

// Turns a template step into a stable step id: stepId, then id, then a
// slugged version of the title. Used to address steps consistently.
export function resolveStepId(step?: StepDefinition): string {
  return (
    step?.stepId ??
    step?.id ??
    String(step?.title ?? "step").toLowerCase().replace(/\s+/g, "_")
  );
}

// Flattens a nested i18n bundle into a "displayed text -> translation key" map.
// Used by the translation hook so tr() can find the i18n key for a label.
export function flattenTranslationValues(
  source: Record<string, unknown>,
  prefix = "",
): Record<string, string> {
  return Object.entries(source).reduce(
    (acc: Record<string, string>, [key, value]) => {
      const fullKey = prefix ? `${prefix}.${key}` : key;
      if (typeof value === "string") {
        acc[value] = fullKey;
        acc[value.trim().toLowerCase()] = fullKey;
      } else if (value && typeof value === "object") {
        Object.assign(
          acc,
          flattenTranslationValues(value as Record<string, unknown>, fullKey),
        );
      }
      return acc;
    },
    {},
  );
}

// Returns true when the template marks the step as a notes and attachments step.
export function isNotesStep(step: TemplateStep): boolean {
  return step.kind === "notes";
}

// Returns true when the step carries the payout outcome registry.
export function isPayoutPoliciesStep(step: TemplateStep): boolean {
  return Boolean(step.outcomeRegistry);
}

// The steps the person fills in. The wizard, the validation and the error
// jump must all use this one list so their step indexes match.
export function getVisibleSteps(steps: TemplateStep[] = []): TemplateStep[] {
  return steps.filter((step) => step.visible !== false);
}

// Collects the deadline rules of all template steps.
export function getDeadlineRules(steps: TemplateStep[] = []): DeadlineRule[] {
  return steps.reduce<DeadlineRule[]>(
    (rules, step) => rules.concat(step.deadlineRules ?? []),
    [],
  );
}

// Field key the backend uses for the event date.
export const EVENT_DATE_FIELD_KEY = "eventDate";

// Finds the template step that asks for the event date.
export function findEventDateField(
  steps: TemplateStep[] = [],
): { stepId: string; field: FieldDefinition } | null {
  for (const step of steps) {
    const field = step.fields?.find((item) => item.key === EVENT_DATE_FIELD_KEY);
    if (field) return { stepId: resolveStepId(step), field };
  }
  return null;
}

// Reads the entered event date from the form data, or null when there is none.
export function getEventDate(
  formData: Record<string, FormValue>,
  steps: TemplateStep[] = [],
): string | null {
  const location = findEventDateField(steps);
  if (!location) return null;

  const stepData = formData[location.stepId];
  if (!stepData || typeof stepData !== "object" || Array.isArray(stepData)) {
    return null;
  }

  const value = stepData[EVENT_DATE_FIELD_KEY];
  return typeof value === "string" && value ? value : null;
}
